// src/pages/Settings/Disciplines/DisciplinesTable.jsx
import React, { useState, useEffect } from "react";
import {
  getAllDisciplines,
  updateDiscipline,
  deleteDiscipline,
  updateOrderDiscipline,
} from "../../../services/disciplinesServices";
import { uploadDisciplinePhoto } from "../../../services/disciplinePhotoServices";
import Button from "../../../components/Button/Button";
import ButtonEdit from "../../../components/Button/ButtonEdit";
import ButtonDelete from "../../../components/Button/ButtonDelete";
import { FaPlus, FaSave, FaTimes } from "react-icons/fa";
import { TbTriangleFilled, TbTriangleInvertedFilled } from "react-icons/tb";
import { ToastContainer, toast } from "react-toastify";
import CreateDisciplineModal from "./CreateDisciplineModal";
import EditDisciplineModal from "./EditDisciplineModal";
import { toAbsolutePhotoUrl } from "../../../services/disciplinePhotoServices";

const NO_IMAGE = "/img/no-image.jpg";

const DisciplinesTable = () => {
  const [disciplines, setDisciplines] = useState([]);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedDisciplineData, setSelectedDisciplineData] = useState(null);

  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [uploadingDiscipline, setUploadingDiscipline] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    fetchDisciplines();
  }, []);

  const fetchDisciplines = async () => {
    try {
      const data = await getAllDisciplines();
      setDisciplines(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Erreur lors de la récupération des disciplines", error);
      toast.error("Impossible de charger les disciplines.");
    }
  };

  const handleDeleteDiscipline = async (discipline) => {
    if (!window.confirm(`Supprimer « ${discipline.nom_discipline} » ?`)) return;
    try {
      await deleteDiscipline(discipline.id);
      toast.success("Discipline supprimée avec succès");
      fetchDisciplines();
    } catch (error) {
      toast.error("Erreur lors de la suppression d’une discipline");
      console.error(error);
    }
  };

  const openEditModal = (discipline) => {
    setSelectedDisciplineData(discipline);
    setIsEditModalOpen(true);
  };
  const closeEditModal = () => {
    setSelectedDisciplineData(null);
    setIsEditModalOpen(false);
  };

  const openUploadModal = (discipline) => {
    setUploadingDiscipline(discipline);
    setIsUploadModalOpen(true);
  };
  const closeUploadModal = () => {
    setUploadingDiscipline(null);
    setSelectedFile(null);
    setIsUploadModalOpen(false);
  };

  const moveDisciplineUp = async (index) => {
    if (index === 0) return;
    const next = [...disciplines];
    [next[index - 1], next[index]] = [next[index], next[index - 1]];
    next[index - 1].ordre_discipline--;
    next[index].ordre_discipline++;
    setDisciplines(next);
    await updateOrderDiscipline(next);
    fetchDisciplines();
  };

  const moveDisciplineDown = async (index) => {
    if (index === disciplines.length - 1) return;
    const next = [...disciplines];
    [next[index + 1], next[index]] = [next[index], next[index + 1]];
    next[index + 1].ordre_discipline++;
    next[index].ordre_discipline--;
    setDisciplines(next);
    await updateOrderDiscipline(next);
    fetchDisciplines();
  };

  const handleFileChange = (e) => setSelectedFile(e.target.files[0]);

  const handleUploadSubmit = async (e) => {
    e.preventDefault();
    if (!selectedFile) {
      toast.error("Veuillez sélectionner une image.");
      return;
    }
    setIsUploading(true);
    try {
      const res = await uploadDisciplinePhoto(
        uploadingDiscipline.id,
        selectedFile,
        {
          format: "webp",
        },
      );
      const newUrl = res?.image_discipline || "";

      setDisciplines((prev) =>
        prev.map((d) =>
          d.id === uploadingDiscipline.id
            ? { ...d, image_discipline: newUrl }
            : d,
        ),
      );
      toast.success("Photo mise à jour avec succès !");
      closeUploadModal();
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de l'upload de l'image.");
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <section className="w-full">
      <div className="mx-auto max-w-7xl px-1 sm:px-1">
        <div className="rounded-2xl overflow-hidden shadow ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/70 dark:bg-slate-900/60 backdrop-blur">
          {/* HEADER dégradé */}
          <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 py-6 sm:px-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h2 className="text-white text-2xl sm:text-3xl font-bold tracking-tight">
                Gestion des Disciplines
              </h2>
              <p className="text-white/90 text-sm sm:text-[15px] mt-1">
                Ajoutez, modifiez, organisez l’ordre et les visuels.
              </p>
            </div>
            <Button
              text="Ajouter une discipline"
              icon={FaPlus}
              onClick={() => setIsCreateModalOpen(true)}
              className="!bg-white !text-slate-900 !hover:bg-white/90 !shadow-md !rounded-xl gap-2"
              hoverLabel="Ajouter une nouvelle discipline"
            />
          </div>

          {/* CONTENT */}
          <div className="p-4 sm:p-6">
            {/* Modales CRUD */}
            {isCreateModalOpen && (
              <CreateDisciplineModal
                isActive={isCreateModalOpen}
                onClose={() => setIsCreateModalOpen(false)}
                refreshDisciplines={fetchDisciplines}
              />
            )}
            {isEditModalOpen && (
              <EditDisciplineModal
                isActive={isEditModalOpen}
                onClose={closeEditModal}
                refreshDisciplines={fetchDisciplines}
                disciplineData={selectedDisciplineData}
              />
            )}

            {/* Upload photo modal */}
            {isUploadModalOpen && (
              <div
                className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 bg-slate-900/60 backdrop-blur-sm"
                onClick={(e) => {
                  if (e.target === e.currentTarget) closeUploadModal();
                }}
                aria-modal="true"
                role="dialog"
                aria-label="Changer la photo de discipline"
              >
                <div className="relative w-full max-w-lg overflow-hidden rounded-2xl shadow-2xl ring-1 ring-slate-200/60 dark:ring-slate-800/60 bg-white/95 dark:bg-slate-900/95">
                  <div className="sticky top-0 z-10 bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 py-4 sm:px-6 flex items-center justify-between">
                    <div>
                      <h3 className="text-white text-lg sm:text-xl font-bold tracking-tight">
                        Changer la photo
                        {uploadingDiscipline
                          ? ` — ${uploadingDiscipline.nom_discipline}`
                          : ""}
                      </h3>
                    </div>
                    <button
                      type="button"
                      onClick={closeUploadModal}
                      className="inline-flex items-center justify-center h-9 w-9 rounded-xl bg-white/15 hover:bg-white/25 transition text-white"
                      title="Fermer"
                    >
                      <FaTimes />
                    </button>
                  </div>

                  <form
                    onSubmit={handleUploadSubmit}
                    className="p-4 sm:p-6 space-y-4"
                  >
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      className="w-full"
                    />
                    <div className="flex justify-end gap-3">
                      <button
                        type="button"
                        onClick={closeUploadModal}
                        className="rounded-xl bg-slate-100 text-slate-800 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700 px-4 py-2 inline-flex items-center gap-2"
                        title="Annuler"
                      >
                        <FaTimes /> Fermer
                      </button>
                      <Button
                        text="Sauvegarder"
                        type="submit"
                        icon={FaSave}
                        className="!rounded-xl gap-2"
                        isLoading={isUploading}
                      />
                    </div>
                  </form>
                </div>
              </div>
            )}

            {/* Grille des disciplines */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {disciplines.map((discipline, index) => {
                const photoSrc =
                  toAbsolutePhotoUrl(
                    discipline.image_discipline?.trim() || "",
                  ) || NO_IMAGE;

                return (
                  <div
                    key={discipline.id}
                    className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white/70 dark:bg-slate-900/40 p-4 shadow-sm transition hover:shadow-md"
                  >
                    <div className="flex justify-between items-start gap-3">
                      {/* Visuel + nom/desc */}
                      <div className="flex items-start gap-3">
                        <div className="flex flex-col items-center">
                          <img
                            src={photoSrc}
                            alt={discipline.nom_discipline}
                            className="w-16 h-16 rounded-xl object-cover cursor-pointer ring-1 ring-slate-200 dark:ring-slate-700"
                            onClick={() => openUploadModal(discipline)}
                            onError={(e) => {
                              e.currentTarget.src = NO_IMAGE;
                            }}
                            referrerPolicy="no-referrer"
                            crossOrigin="anonymous"
                          />
                          <button
                            type="button"
                            onClick={() => openUploadModal(discipline)}
                            className="mt-1 text-[11px] text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                          >
                            Modifier la photo
                          </button>
                        </div>
                        <div>
                          <h3 className="font-semibold text-slate-900 dark:text-slate-100">
                            {discipline.nom_discipline}
                          </h3>
                          <p className="text-sm text-slate-700 dark:text-slate-300">
                            {discipline.description_discipline}
                          </p>
                        </div>
                      </div>

                      {/* Ordre */}
                      <div className="flex items-center">
                        <div className="flex flex-col -my-1">
                          <button
                            onClick={() => moveDisciplineUp(index)}
                            className="p-1 text-slate-500 hover:text-slate-800 dark:hover:text-white transition"
                            title="Monter"
                            type="button"
                          >
                            <TbTriangleFilled />
                          </button>
                          <button
                            onClick={() => moveDisciplineDown(index)}
                            className="p-1 text-slate-500 hover:text-slate-800 dark:hover:text-white transition"
                            title="Descendre"
                            type="button"
                          >
                            <TbTriangleInvertedFilled />
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Contenu HTML */}
                    {discipline.contenu_discipline && (
                      <div
                        className="prose prose-sm max-w-none mt-3 text-slate-700 dark:text-slate-300
                        dark:[&_strong]:text-slate-100 dark:[&_b]:text-slate-100"
                        dangerouslySetInnerHTML={{
                          __html: discipline.contenu_discipline,
                        }}
                      />
                    )}

                    {/* Actions */}
                    <div className="mt-4 flex justify-end gap-2">
                      <ButtonEdit onClick={() => openEditModal(discipline)} />
                      <ButtonDelete
                        onClick={() => handleDeleteDiscipline(discipline)}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <ToastContainer />
        </div>
      </div>
    </section>
  );
};

export default DisciplinesTable;
